/**
 * Org preview access rules ("work as parent" mode).
 *
 * Previewing an org swaps the user's effective access to the previewed org's
 * config flags (see Loader.assumeRole). That faithfully shows what the org's
 * users see — but it also locks parents out of the library features
 * (isSelfService-gated: creatives, targeting, tags, audience, retail stores)
 * when building campaigns on behalf of a non-self-service advertiser.
 *
 * Preview therefore has two modes:
 * - faithful: exactly the previewed org's flags (inspect their configuration)
 * - workAsParent: the previewed org's flags PLUS isSelfService — only when the
 *   REAL user has isSelfService themselves. Preview is a privileged action, so
 *   this never leaks access to the advertiser's own users.
 */

export const PREVIEW_MODES = { FAITHFUL: 'faithful', WORK_AS_PARENT: 'workAsParent' };

/**
 * Compute the effective permission + permify roles for a preview session.
 *
 * @param {Object} args
 * @param {Object} [args.previewConfig] – the previewed org's config flags
 * @param {string} [args.level] – the previewed org's level (added to roles)
 * @param {string} [args.previewMode] – PREVIEW_MODES value (default faithful)
 * @param {boolean} [args.realUserIsSelfService] – does the REAL (pre-swap)
 *        user have isSelfService? (currentUser.defaultRole.permission.isSelfService)
 * @returns {{ permission: Object, roles: string[] }}
 */
export const computePreviewAccess = ({
  previewConfig,
  level,
  previewMode,
  realUserIsSelfService,
} = {}) => {
  const config = previewConfig || {};
  const allowSelfService =
    previewMode === PREVIEW_MODES.WORK_AS_PARENT && !!realUserIsSelfService;

  const permission = allowSelfService ? { ...config, isSelfService: true } : { ...config };

  const roles = Object.keys(permission)
    .filter((key) => key.startsWith('is'))
    .filter((key) => permission[key]);
  if (level) roles.push(level);

  return { permission, roles };
};
